/**
 * StoresLoader - A reusable utility class for loading store data from S3
 * Supports caching and loading stores for different retailers
 */

import type { RetailerType } from "../config/retailers";

const S3_BASE_URL = "https://dealhawk-app.s3.us-east-2.amazonaws.com";

// Retailers backed by a physical-store catalog. Admin and customer store
// pickers share this list so supported catalogs stay in sync across screens.
export const RETAILERS_WITH_STORES = [
  "walmart",
  "home_depot",
  "target",
  "dollar_general",
  "bestbuy",
  "costco",
  "lowes",
  "tractor_supply",
  "northern_tool",
  "samsclub",
] as const satisfies readonly RetailerType[];

type RetailerWithStores = (typeof RETAILERS_WITH_STORES)[number];
export type { RetailerWithStores };

interface StoreCache<T = any> {
  data: T[];
  timestamp: number;
}

class StoresLoader {
  private cache: Map<RetailerWithStores, StoreCache> = new Map();
  private loadingPromises: Map<RetailerWithStores, Promise<any[]>> = new Map();
  private readonly CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

  /**
   * Get the S3 URL for a retailer's stores file
   */
  private cacheBust(url: string): string {
    return `${url}?t=${Date.now()}`;
  }

  private getStoreUrl(retailer: RetailerWithStores): string {
    const fileNameMap: Record<RetailerWithStores, string> = {
      walmart: "walmart-stores.json",
      home_depot: "home-depot-stores.json",
      dollar_general: "dollar-general-stores.json",
      target: "target-stores.json",
      bestbuy: "bestbuy-stores.json",
      costco: "costco-stores.json",
      lowes: "lowes-stores.json",
      tractor_supply: "tractor-supply-stores.json",
      northern_tool: "northern-tool-stores.json",
      samsclub: "samsclub-stores.json",
    };

    return `${S3_BASE_URL}/${fileNameMap[retailer]}`;
  }

  /**
   * Check if cached data is still valid
   */
  private isCacheValid(retailer: RetailerWithStores): boolean {
    const cached = this.cache.get(retailer);
    if (!cached) return false;

    const age = Date.now() - cached.timestamp;
    return age < this.CACHE_DURATION;
  }

  /**
   * Load stores for a specific retailer from S3
   * @param retailer - The retailer type (woot is not supported as it doesn't have stores)
   * @param forceRefresh - Force refresh even if cache is valid
   * @returns Promise resolving to stores (T is already the array type, e.g., WalmartStore[])
   */
  async loadStores<T extends any[] = any[]>(
    retailer: RetailerWithStores,
    forceRefresh = false
  ): Promise<T> {
    // Return cached data if valid and not forcing refresh
    if (!forceRefresh && this.isCacheValid(retailer)) {
      return this.cache.get(retailer)!.data as T;
    }

    // Return existing loading promise if already loading
    if (this.loadingPromises.has(retailer)) {
      return this.loadingPromises.get(retailer)! as Promise<T>;
    }

    // Start loading
    const loadPromise = this.fetchStores<T>(retailer, forceRefresh);
    this.loadingPromises.set(retailer, loadPromise);

    try {
      const stores = await loadPromise;
      this.cache.set(retailer, {
        data: stores,
        timestamp: Date.now(),
      });
      return stores;
    } catch (error) {
      // If fetch fails and we have cached data, return cached data as fallback
      const cached = this.cache.get(retailer);
      if (cached) {
        console.warn(
          `Failed to fetch ${retailer} stores from S3, using cached data:`,
          error
        );
        return cached.data as T;
      }
      throw error;
    } finally {
      this.loadingPromises.delete(retailer);
    }
  }

  /**
   * Fetch stores from S3
   */
  // Slim catalogs carry only the fields the app reads (id, address, coordinates). Home Depot's
  // full file is ~2.9 MB of hours/services/source URLs; the slim copy is ~0.45 MB. Generated by
  // server/scripts/slim-store-catalog.py; the full file remains the fallback.
  private getSlimStoreUrl(retailer: RetailerWithStores): string | null {
    const slimFileNameMap: Partial<Record<RetailerWithStores, string>> = {
      home_depot: "home-depot-stores.slim.json",
    };
    const fileName = slimFileNameMap[retailer];
    return fileName ? `${S3_BASE_URL}/${fileName}` : null;
  }

  private async fetchStores<T extends any[] = any[]>(
    retailer: RetailerWithStores,
    forceRefresh = false
  ): Promise<T> {
    const slimUrl = this.getSlimStoreUrl(retailer);
    if (slimUrl) {
      try {
        const slim = await this.fetchStoreCatalog<T>(retailer, forceRefresh ? this.cacheBust(slimUrl) : slimUrl);
        if (Array.isArray(slim) && slim.length > 0) return slim;
      } catch (slimError) {
        console.warn(`Slim ${retailer} catalog unavailable, using the full file:`, slimError);
      }
    }
    const primaryUrl = forceRefresh
      ? this.cacheBust(this.getStoreUrl(retailer))
      : this.getStoreUrl(retailer);

    try {
      return await this.fetchStoreCatalog<T>(retailer, primaryUrl);
    } catch (primaryError) {
      throw new Error(
        `Error loading ${retailer} stores from ${primaryUrl}: ${
          primaryError instanceof Error ? primaryError.message : String(primaryError)
        }`
      );
    }
  }

  private async fetchStoreCatalog<T extends any[] = any[]>(
    retailer: RetailerWithStores,
    url: string
  ): Promise<T> {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${retailer} stores: HTTP ${response.status}`);
    }

    const data = await response.json();
    if (!Array.isArray(data)) {
      throw new Error(`Invalid data format for ${retailer} stores: expected array`);
    }

    if (retailer === "samsclub") {
      return data.map(normalizeSamsClubStore) as T;
    }

    return data as T;
  }

  /**
   * Clear cache for a specific retailer or all retailers
   */
  clearCache(retailer?: RetailerWithStores): void {
    if (retailer) {
      this.cache.delete(retailer);
      this.loadingPromises.delete(retailer);
    } else {
      this.cache.clear();
      this.loadingPromises.clear();
    }
  }

  /**
   * Get cached stores without fetching (returns null if not cached)
   */
  getCachedStores<T extends any[] = any[]>(retailer: RetailerWithStores): T | null {
    const cached = this.cache.get(retailer);
    if (cached && this.isCacheValid(retailer)) {
      return cached.data as T;
    }
    return null;
  }
}

/**
 * Sam's Club's catalog uses Walmart-style fields (store_id, address, postal_code).
 * Alias them onto the local-clearance shape (store_number, street, zip) so the
 * Sam's Club pickers share the Northern Tool / Tractor Supply code paths. The
 * original fields stay on the object.
 */
export function normalizeSamsClubStore(store: any): any {
  if (!store || typeof store !== "object") return store;
  const storeId = String(store.store_id ?? store.store_number ?? "");
  const latitude = store.latitude ?? store.geo?.lat;
  const longitude = store.longitude ?? store.geo?.long;
  return {
    ...store,
    store_number: storeId,
    street: store.street ?? store.address ?? "",
    zip: store.zip ?? store.postal_code ?? "",
    label: store.label || store.name || `Sam's Club #${storeId}`,
    latitude: latitude == null ? latitude : Number(latitude),
    longitude: longitude == null ? longitude : Number(longitude),
  };
}

// Export singleton instance
export const storesLoader = new StoresLoader();

// Export convenience functions for each retailer
export const loadWalmartStores = <T extends any[] = any[]>(
  forceRefresh = false
) => storesLoader.loadStores<T>("walmart", forceRefresh);

export const loadHomeDepotStores = <T extends any[] = any[]>(
  forceRefresh = false
) => storesLoader.loadStores<T>("home_depot", forceRefresh);

export const loadDollarGeneralStores = <T extends any[] = any[]>(
  forceRefresh = false
) => storesLoader.loadStores<T>("dollar_general", forceRefresh);

export const loadTargetStores = <T extends any[] = any[]>(
  forceRefresh = false
) => storesLoader.loadStores<T>("target", forceRefresh);

export const loadBestBuyStores = <T extends any[] = any[]>(
  forceRefresh = false
) => storesLoader.loadStores<T>("bestbuy", forceRefresh);

export const loadCostcoStores = <T extends any[] = any[]>(
  forceRefresh = false
) => storesLoader.loadStores<T>("costco", forceRefresh);

export const loadLowesStores = <T extends any[] = any[]>(
  forceRefresh = false
) => storesLoader.loadStores<T>("lowes", forceRefresh);

export const loadTractorSupplyStores = <T extends any[] = any[]>(
  forceRefresh = false
) => storesLoader.loadStores<T>("tractor_supply", forceRefresh);

export const loadNorthernToolStores = <T extends any[] = any[]>(
  forceRefresh = false
) => storesLoader.loadStores<T>("northern_tool", forceRefresh);

export const loadSamsClubStores = <T extends any[] = any[]>(
  forceRefresh = false
) => storesLoader.loadStores<T>("samsclub", forceRefresh);

export const STORES_REFRESH_COOLDOWN_MS = 60 * 60 * 1000;
const REFRESH_KEY = "dealhawk-stores-refresh:";

export class RefreshCooldownError extends Error {
  remainingMs: number;
  constructor(remainingMs: number) {
    super("Refresh cooldown");
    this.name = "RefreshCooldownError";
    this.remainingMs = remainingMs;
  }
}

export function formatRefreshCooldown(ms: number): string {
  const mins = Math.ceil(ms / 60000);
  if (mins >= 60) return `${Math.ceil(mins / 60)}h`;
  return `${mins}m`;
}

export function getStoresRefreshStatus(retailer: RetailerWithStores) {
  try {
    const last = Number(localStorage.getItem(`${REFRESH_KEY}${retailer}`) || 0);
    const remaining = STORES_REFRESH_COOLDOWN_MS - (Date.now() - last);
    return { canRefresh: remaining <= 0, remainingMs: Math.max(0, remaining) };
  } catch {
    return { canRefresh: true, remainingMs: 0 };
  }
}

export function catalogStoreId(store: any, retailer: RetailerWithStores): string {
  switch (retailer) {
    case "walmart":
      return String(store.store_id || "");
    case "home_depot": {
      const id = String(store.store_number || store.Id || "");
      return id.replace(/^0+/, "") || id;
    }
    case "dollar_general":
      return String(store.storeNumber || "");
    case "target":
    case "lowes":
      return String(store["Store No."] || "");
    case "tractor_supply":
    case "northern_tool":
    case "samsclub":
      return String(store.store_number || "").replace(/^0+/, "") || String(store.store_number || "");
    case "bestbuy":
      return String(store.store_number || "");
    case "costco":
      return store.warehouse_number
        ? `${store.warehouse_number}-wh`
        : String(store.address || "");
    default:
      return "";
  }
}

export async function refreshStoresCatalog<T extends any[]>(
  retailer: RetailerWithStores,
  currentStores: T
): Promise<{ stores: T; newStoreIds: string[] }> {
  const status = getStoresRefreshStatus(retailer);
  if (!status.canRefresh) throw new RefreshCooldownError(status.remainingMs);

  const previousIds = new Set(
    currentStores.map((s) => catalogStoreId(s, retailer)).filter(Boolean)
  );
  storesLoader.clearCache(retailer);
  const fresh = await storesLoader.loadStores<T>(retailer, true);
  const newStoreIds = fresh
    .map((s) => catalogStoreId(s, retailer))
    .filter((id) => id && !previousIds.has(id));

  localStorage.setItem(`${REFRESH_KEY}${retailer}`, String(Date.now()));
  return { stores: fresh, newStoreIds };
}
